import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import type { WorkflowStatus } from '@/api/workflows';
import { statusColor } from '@/originator/statusUtils';

type Props = {
  status: WorkflowStatus;
  /**
   * The row's own IsActive flag. Only consulted for Approved rows — what it
   * means there depends on {@link scope}. Leave undefined for other statuses.
   */
  isActive?: boolean;
  /**
   * What `status` describes. Approved + not-active carries OPPOSITE meanings
   * for the two, which is why the caller has to say which it is holding:
   *
   * - `template` — the workflow has been approved but the Approver has not
   *   pushed it live yet, so it is **Pending Publishing**.
   * - `version`  — this version was approved and has since been superseded by
   *   a newer one, so it is **Archived**.
   *
   * Required on purpose. It used to default to version semantics, and the
   * Workflow Details page (a template) simply never passed `isActive` at all,
   * so a Pending-Publishing workflow announced itself as "Published".
   */
  scope: 'template' | 'version';
  /**
   * A newly-approved version is staged and awaiting the Approver's publish
   * toggle (`PendingApprovedVersionNumber` is set). Meaning depends on scope:
   *
   * - `template` — the template as a whole has a staged version somewhere
   *   (may or may not be the one on screen).
   * - `version`  — THIS row IS that staged version (caller compares its own
   *   `versionNumber` against the template's `pendingApprovedVersionNumber`).
   *
   * This is a SEPARATE signal from `isActive`, and both can be true at once.
   * When a re-approval lands on a live workflow the domain leaves IsActive on
   * the version still serving advisors, so `isActive` alone reports on the
   * PREDECESSOR — yet these pages render the template's current steps, which
   * are the staged version's. Without this the Workflow Details page showed a
   * green "Published" pill above v4's content while v4 was still waiting to be
   * published, and a version-scoped view of that same staged row (Approved +
   * not-yet-active) read as "Archived" — indistinguishable from a version that
   * really had been superseded.
   */
  hasPendingApproval?: boolean;
  /**
   * Only meaningful when `scope === 'version'`: whether THIS row is
   * `template.currentVersionNumber` — the latest approved content — rather
   * than one genuinely superseded by a newer approved version. Approved +
   * not-active + this true means the Approver took the whole workflow
   * offline (`SetActiveAsync(false)`), which never touches a version row's
   * own `IsActive`/`Status`, so from the row's own fields alone this is
   * indistinguishable from a truly archived (superseded) version. Caller
   * compares its own `versionNumber` against the template's
   * `currentVersionNumber` — same pattern as `hasPendingApproval`.
   */
  isCurrentVersion?: boolean;
  /**
   * Render decision-verb labels ("Approved" / "Rejected") instead of the
   * workflow-state labels ("Published" / "Rejected"). Used by Recent Decisions
   * where the column describes WHAT the approver did, not the workflow's
   * current state. Default false (workflow-state semantics).
   */
  decisionMode?: boolean;
};

/**
 * Single source of truth for status pills across the app — Workflows card, Version
 * History table, Workflow Details, Recent Decisions. Encodes the one branching rule
 * the design has ("Approved + not active = Archived") so it can't disagree between
 * pages.
 */
export function StatusTag({
  status,
  isActive,
  scope,
  hasPendingApproval,
  isCurrentVersion,
  decisionMode,
}: Props) {
  const { t } = useTranslation();
  // Recent Decisions semantics — match the approver dashboard tab labels.
  // Approved + active → "Published" (Published Workflows tab). Approved + not
  // active → "Approved" (Approved Workflows tab). Rejected → "Rejected". Used
  // in place of the Archived label so the recent-decisions row mirrors the
  // tab the workflow currently sits in.
  if (decisionMode) {
    if (status === 'Approved' && isActive === true) {
      return <Tag color={statusColor(status)}>{t('workflow.statusLabels.Approved')}</Tag>;
    }
    if (status === 'Approved' || status === 'Rejected') {
      return <Tag color={statusColor(status)}>{t(`workflow.decisionLabels.${status}`)}</Tag>;
    }
  }
  // A staged approval outranks IsActive: the version on screen is the one
  // waiting to go live, whatever is still being served underneath it. Applies
  // to both scopes — see the `hasPendingApproval` doc for what it means on
  // each — EXCEPT a version row that is itself active: a version can't be
  // both live and the not-yet-promoted successor waiting behind it, so a
  // stale/contradictory `hasPendingApproval` there is ignored.
  if (status === 'Approved' && hasPendingApproval && (scope === 'template' || isActive !== true)) {
    return (
      <Tag color={statusColor('PendingPublishing')}>
        {t('workflow.statusLabels.PendingPublishing')}
      </Tag>
    );
  }
  if (status === 'Approved' && isActive === false) {
    // Approved but not live. For a template that means the Approver has still
    // to publish it — the same state the dashboard's "Pending Publishing" tab
    // holds, and it uses the same label and colour so the card badge and this
    // pill agree. For a version it means superseded.
    if (scope === 'template') {
      return (
        <Tag color={statusColor('PendingPublishing')}>
          {t('workflow.statusLabels.PendingPublishing')}
        </Tag>
      );
    }
    // Still the current approved version — just taken offline by the
    // Approver's unpublish toggle — is NOT the same as superseded by a
    // newer one. See `isCurrentVersion`'s doc.
    if (isCurrentVersion) {
      return (
        <Tag color={statusColor('Unpublished')}>{t('workflow.versions.statusUnpublished')}</Tag>
      );
    }
    return <Tag color="default">{t('workflow.versions.statusArchived')}</Tag>;
  }
  return <Tag color={statusColor(status)}>{t(`workflow.statusLabels.${status}`)}</Tag>;
}
